<template>
  <div class="modal fade" :id="id" data-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog" role="document">
      <form @submit.prevent="submitData" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ !isEdit ? $t('taxes.creation') : $t('taxes.modification') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Close">
              <span class="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                    fill="currentColor" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                    fill="currentColor" />
                </svg>
              </span>
            </div>
          </div>
          <div class="modal-body">
            <RequiredText />
            <div class="d-flex flex-column mb-8 fv-row">
              <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                <span class="required">{{ $t('taxes.title') }}</span>
              </label>
              <input type="text" v-model="formData.title" class="form-control form-control-solid"  required
                :placeholder="$t('taxes.title_placeholder')" />
            </div>
            <div class="d-flex flex-column mb-8 fv-row">
                <Select2
                  :parent-id="'#'+id"
                  containerClass="mb-2"
                  :name="'tax_type'+id"
                  :multiple="false"
                  :label="$t('taxes.type')"
                  labelClass="d-flex align-items-center fs-6 fw-bold mb-2"
                  selectClass="form-select form-select-solid kt_select2"
                  :isRequired="true"
                  :placeholderText="$t('taxes.type_placeholder')"
                  v-model="formData.type">
                  <option value="amount">{{ $t('taxes.type_amount') }}</option>
                  <option value="percent">{{ $t('taxes.type_percent') }}</option>
                </Select2>
            </div>
            <div class="d-flex flex-column mb-8 fv-row" v-if="formData.type == 'amount'">
              <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                <span class="required">{{ $t('taxes.amount') }} ({{ currency?.iso }})</span>
              </label>
              <input type="text"  @input="formatInput" v-model="formData.amount"  class="form-control form-control-solid"
                   required :placeholder="$t('taxes.amount_placeholder')" />
            </div>
            <div class="d-flex flex-column mb-8 fv-row" v-if="formData.type == 'percent'">
              <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                <span class="required">{{ $t('taxes.percent') }}</span>
              </label>
              <div class="input-group mb-5">
                <input type="number" v-model="formData.amount" @keypress="onlyNumber" step=".01" class="form-control form-control-solid"
                   required :placeholder="$t('taxes.percent_placeholder')" />
                <span class="input-group-text" id="basic-addon2">%</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
            <Button btn-class="btn-sm" type="submit" id="submit_btn" btn-text="button.save" size="7px 30px" :is-loading="isLoadingTax"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>

import { computed, ref, defineProps } from "vue";
import Select2 from "@/components/Select2.vue";
import {useStore} from "vuex";
import RequiredText from "@/components/RequiredText"
import Button from "@/components/Button"

const store = useStore()
const props = defineProps({
  tax: {
    type: Object,
    default: {
      title: '',
      type: 'percent',
      amount: 0,
    }
  },
  id: {
      type: String,
      required: true
  },
  isEdit: { type: Boolean, default: false }
})

const emits = defineEmits(['load', 'addTax'])

const currency = computed(() => store.getters['company/getCompanyCurrency'])
const formData = ref(props.tax)
const isLoadingTax = ref(false)

function submitData(){
  if(!props.isEdit){
    createTaxData()
  }else{
    updateTaxData()
  }
}

function createTaxData() {
  let amount = formData.value.amount;
  if (typeof amount === 'string') {
    amount = amount.replace(/\s/g, '');
  }
  formData.value.amount = parseFloat(amount);

  isLoadingTax.value = true
  store.dispatch('taxes/createTaxe', formData.value)
    .then((response) => {
      emits('addTax', response.data?.taxe?.id)
      emits('load')
      isLoadingTax.value = false
      $('#'+props.id).modal('toggle')
      formData.value.title = ''
      formData.value.type = 'percent'
      formData.value.amount = 0
      window.toastr['success'](i18n.global.t('notification.taxe_created'))
    })
    .catch(err => {
      isLoadingTax.value = false
    })
}

function updateTaxData() {
  let amount = formData.value.amount;
  if (typeof amount === 'string') {
    amount = amount.replace(/\s/g, '');
  }
  formData.value.amount = parseFloat(amount);

  isLoadingTax.value = true
  store.dispatch('taxes/updateTaxe', formData.value)
    .then((response) => {
      emits('load')
      isLoadingTax.value = false
      $('#'+props.id).modal('toggle')
      formData.value.id = ''
      formData.value.type = 'percent'
      formData.value.amount = 0
      formData.value.title = ''
      window.toastr['success'](i18n.global.t('notification.taxe_updated'))
    })
    .catch(err => {
      isLoadingTax.value = false
    })
}

function onlyNumber($event) {
  let keyCode = ($event.keyCode ? $event.keyCode : $event.which);
  if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { // 46 is dot
    $event.preventDefault();
  }
}

const formattedAmount = ref('');

const formatInput = ($event) => {
  let value = $event.target.value;
  value = value.replace(/[^0-9.]/g, '');
  let formattedValue = value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  formData.value.amount = formattedValue;
};

function closeModal(){
  $('#'+props.id).modal('toggle')
}
</script>

<style scoped></style>
